import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import {
  setToken,
  getAccessTokenFromStorage,
  removeAuthTokenFromStorage,
  PUBLIC_PAGES,
  LOGIN_ERR_MSG,
} from '../utils';
import { generateRandomString, pkceChallengeFromVerifier } from '../utils/generateCodeChallenge';
import {
  getAuthToken,
  getCodeFromConcent,
  getConsentChallenge,
  getLoginChallenge,
  getRedirectURL,
  buildAuthorizationUrl,
} from '../services';
import { User } from '../@types';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { readClickIds } from '../utils/marketing/allAdsPlatformTracking';

export interface AuthContextType {
  auth: AuthState;
  userData: User | null;
  login: () => Promise<boolean>;
  logout: () => void;
  setUserData: Dispatch<SetStateAction<User | null>>;
  userLoading: boolean;
}

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { push, pathname } = useRouter();

  const [userData, setUserData] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState<boolean>(true);

  const [auth, setAuth] = useState<AuthState>({
    isAuthenticated: false,
    token: null,
  });

  const { enqueueSnackbar } = useSnackbar();

  const setSession = (accessToken: string, refreshToken: string, codeVerifier: string) => {
    setToken(accessToken, refreshToken, codeVerifier);
    setAuth({ isAuthenticated: true, token: accessToken });
  };

  const login = async (): Promise<boolean> => {
    try {
      // PKCE setup
      const codeVerifier = generateRandomString();
      const codeChallenge = await pkceChallengeFromVerifier(codeVerifier);
      // Persist verifier for callback exchange
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('pkce_code_verifier', codeVerifier);
        // Backup in localStorage to handle rare cases where sessionStorage is unavailable after redirects
        try {
          window.localStorage.setItem('pkce_code_verifier_bak', codeVerifier);
        } catch {}
      }

      // Optional: store attribution before redirect, since we won't return here
      readClickIds();

      // Generate state and persist for optional verification
      const stateVal = generateRandomString();
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('oauth_state', stateVal);
        try {
          window.localStorage.setItem('oauth_state_bak', stateVal);
        } catch {}
      }

      // Full-page navigation so Hydra can set CSRF cookie properly
      const authUrl = buildAuthorizationUrl(codeChallenge, stateVal);
      window.location.href = authUrl;
      // This function will not complete due to navigation; return true to satisfy callers
      return true;
    } catch (error: any) {
      if (error.response?.data?.message.includes(LOGIN_ERR_MSG)) {
        enqueueSnackbar(error.response?.data?.message, { variant: 'error' });
      }
      console.error('Login failed', error);
      return false;
    }
  };

  const handleLogoutRedirect = useCallback(() => {
    push('/');
  }, [push]);

  const logout = async () => {
    // Always clear local session state first
    setAuth({ isAuthenticated: false, token: null });
    removeAuthTokenFromStorage();

    // Prefer backend-orchestrated combined logout to minimize redirects and browser parsing.
    if (typeof window !== 'undefined') {
      window.location.href = '/api/auth/combined-logout';
      return;
    }
    handleLogoutRedirect();
  };

  useEffect(() => {
    const token = getAccessTokenFromStorage();
    if (token && userLoading) {
      setAuth({ isAuthenticated: true, token });
      setUserLoading(false);
    } else if (!PUBLIC_PAGES.includes(pathname) && userLoading) {
      // If an OAuth/PKCE flow is in progress, avoid redirecting to login.
      let authInProgress = false;
      try {
        const ss = typeof window !== 'undefined' ? window.sessionStorage : undefined;
        const ls = typeof window !== 'undefined' ? window.localStorage : undefined;
        authInProgress = !!(
          (ss && (ss.getItem('oauth_state') || ss.getItem('pkce_code_verifier'))) ||
          (ls && (ls.getItem('oauth_state_bak') || ls.getItem('pkce_code_verifier_bak')))
        );
      } catch {}

      if (!authInProgress) {
        handleLogoutRedirect();
      }
    } else if (userLoading && ['/', '/pricing', '/alpha-pricing'].includes(pathname)) {
      setUserLoading(false);
    }
  }, [pathname, userLoading, handleLogoutRedirect]);

  // Listen for token changes
  useEffect(() => {
    const handleTokenChange = () => {
      const token = getAccessTokenFromStorage();
      setAuth((prevAuth) => {
        // Only update if token actually changed
        if (prevAuth.token !== token) {
          return { isAuthenticated: !!token, token };
        }
        return prevAuth;
      });
    };

    // Listen for storage events (cross-tab changes)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'access_token') {
        handleTokenChange();
      }
    };

    // Initial check
    handleTokenChange();

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ auth, login, logout, userData, setUserData, userLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
